/**
 * Filling the shareholders' shared archive — 17 Sep 2026.
 *
 * Admin-only, as the rules say: this is the company speaking to its owners, and
 * an ordinary staff login has no business adding to it. Everything filed here
 * is visible to every shareholder the moment it is uploaded — there is no draft
 * state and no per-holder targeting, because "the documents that belong to all
 * of us" is exactly what the archive is for.
 *
 * ## PDFs, checked twice
 *
 * Here, so the picker only offers them and a mistake is caught before the
 * upload; and in `storage.rules`, which is the one that counts. A share
 * register that accepted an HTML file would be serving it from our own storage
 * domain, which is a stored-XSS delivery mechanism wearing a document's name.
 */
import { useEffect, useRef, useState } from 'react'
import { fmtFileSize } from '@/shared/util/files'
import { formatDate } from '@/shared/util/dates'
import {
  addShareholderDoc,
  deleteShareholderDoc,
  redateShareholderDoc,
  renameShareholderDoc,
  downloadShareholderDoc,
  subscribeToShareholderDocs,
  type ShareholderDoc,
} from './repository/shareholderDocsRepository'
import './Mail.css'

export function ArchivePanel({ me }: { me: string }) {
  const [docs, setDocs] = useState<ShareholderDoc[]>([])
  const [busy, setBusy] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  useEffect(
    () => subscribeToShareholderDocs(setDocs, (e) => setError(e.message)),
    [],
  )

  const upload = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setBusy('upload')
    setError(null)
    try {
      await addShareholderDoc(file, note.trim(), me)
      setNote('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The upload failed.')
    } finally {
      setBusy('')
    }
  }

  const open = async (d: ShareholderDoc) => {
    setBusy(d.id)
    setError(null)
    try {
      await downloadShareholderDoc(d.id, d.name)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That document could not be opened.')
    } finally {
      setBusy('')
    }
  }

  const remove = async (d: ShareholderDoc) => {
    if (!window.confirm(`Remove "${d.name}" from the archive? Every shareholder loses access.`)) {
      return
    }
    setBusy(d.id)
    try {
      await deleteShareholderDoc(d)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'It could not be removed.')
    } finally {
      setBusy('')
    }
  }

  const rename = async (d: ShareholderDoc) => {
    const name = window.prompt('Name as shareholders see it', d.name)
    if (name === null) return
    const next = window.prompt('One line saying what it is (optional)', d.note)
    if (next === null) return
    await renameShareholderDoc(d.id, name.trim() || d.name, next.trim())
  }

  return (
    <div className="mail">
      <div className="mail__head">
        <div>
          <h2 className="mail__title">Shareholder archive</h2>
          <p className="mail__lede">
            The shareholder agreement, protokoller from general meetings — visible to every
            shareholder as soon as it is here. PDFs only, up to 20 MB. The date is the document's
            own, not the upload's, and the list is newest first.
          </p>
        </div>
      </div>

      <div className="arch__add">
        <label className="field arch__note">
          <span className="card-label">What is it? (optional, shown under the name)</span>
          <input
            className="input"
            value={note}
            placeholder="Ordinary general meeting, 14 May 2026"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <input
          ref={picker}
          type="file"
          accept="application/pdf"
          hidden
          onChange={(e) => {
            void upload(e.target.files)
            e.target.value = ''
          }}
        />
        <button
          className="btn btn--primary"
          disabled={busy === 'upload'}
          onClick={() => picker.current?.click()}
        >
          {busy === 'upload' ? 'Uploading…' : 'Add a PDF'}
        </button>
      </div>

      {error && <p className="recform__error">{error}</p>}

      {docs.length === 0 ? (
        <p className="empty-note">Nothing has been filed yet.</p>
      ) : (
        <div className="mail__table" role="table" aria-label="Archived documents">
          <div className="mail__row mail__row--head arch__row" role="row">
            <span role="columnheader">Document</span>
            <span role="columnheader">Date</span>
            <span role="columnheader">Size</span>
            <span role="columnheader" />
          </div>
          {docs.map((d) => (
            <div className="mail__row arch__row" role="row" key={d.id}>
              <span role="cell" className="mail__subject">
                {d.name}
                <span className="micro">
                  {d.note || '—'}
                  {!d.path && ' · upload did not finish'}
                </span>
              </span>
              {/* Editable, and it is what the list sorts on. The day of the
                  meeting is rarely the day somebody got round to filing it, and
                  the shareholder reading the list means the former. */}
              <span role="cell" className="mail__when">
                <input
                  className="input arch__date"
                  type="date"
                  value={d.date}
                  onChange={(e) => void redateShareholderDoc(d.id, e.target.value)}
                />
                <span className="micro" title="When it reached us">
                  filed {d.uploadedAt ? formatDate(d.uploadedAt.slice(0, 10)) : '—'} ·{' '}
                  {d.uploadedBy}
                </span>
              </span>
              <span role="cell">{fmtFileSize(d.size)}</span>
              <span role="cell" className="arch__acts">
                <button
                  className="btn btn--ghost btn--tiny"
                  disabled={busy === d.id || !d.path}
                  onClick={() => void open(d)}
                >
                  Open
                </button>
                <button className="btn btn--ghost btn--tiny" onClick={() => void rename(d)}>
                  Rename
                </button>
                <button
                  className="btn btn--ghost btn--tiny"
                  disabled={busy === d.id}
                  onClick={() => void remove(d)}
                >
                  Remove
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
